/**
 * Deliverables-tab fold: the day's deliverables TOTALED across every school.
 *
 * The packing team preps identical items for many locations in advance
 * (10 schools ordering the same packages, fridged the day before) — so this
 * view answers "how many of each thing do we pack in total", not "what does
 * each school get" (that's the per-school card on /orders and the printed
 * delivery sheet).
 *
 * Identical deliverables merge across campuses via `mergeCampusesMealtimes`
 * (same name, serving, unit, temperature, bulk mode → one row, summed qty), grouped
 * by day then mealtime. Rows open the standard deliverable detail panel,
 * scaled to the MERGED quantity — the batch actually being packed.
 */

import type { KitchenShipmentSummary } from '@/api/kitchen-orders'
import type { ShoppingListForecastRecipe } from '@/api/shopping-list'
import { mergeCampusesMilk, type MergedMilkRow } from '@/lib/chefs/milk'
import { isoDate } from '@/lib/chefs/service-days'
import { shipmentsForDay, shipmentsToCampuses } from '@/lib/chefs/shipment-to-campus'
import { computeTotalMeals, mergeCampusesMealtimes } from '@/lib/chefs/totals'
import type { MealtimeBlock } from '@/lib/chefs/types'
import { MEALTIME_ORDER } from '@/lib/chefs/types'

export interface DeliverablesDayGroup {
  /** `YYYY-MM-DD`. */
  iso: string
  date: Date
  /** TRUE meal count across every school this day (never re-derived from
   *  the merged rows — see `computeMealtimeMeals`). */
  meals: number
  /** How many schools contributed — the "packing for N schools" context. */
  schoolCount: number
  /** MEALTIME_ORDER-sorted; deliverables merged across schools. */
  blocks: MealtimeBlock[]
  /** The day's milk summed across schools: milk rides on each campus's milk card, not in `blocks` (PROD-1502). */
  milk: MergedMilkRow[]
  forecastItems: ForecastMenuItem[]
}

/**
 * Build the day-grouped deliverables list for the range. Every service day keeps
 * a group — an empty `blocks` renders the per-day empty treatment instead
 * of the day silently vanishing.
 */
export function buildDeliverablesDayGroups(
  serviceDays: ReadonlyArray<{ date: Date }>,
  shipments: ReadonlyArray<KitchenShipmentSummary>,
  forecastItems: ReadonlyArray<ShoppingListForecastRecipe> = [],
): DeliverablesDayGroup[] {
  return serviceDays.map((day) => {
    const iso = isoDate(day.date)
    const campuses = shipmentsToCampuses(shipmentsForDay(shipments, iso))
    const blocks = mergeCampusesMealtimes(campuses).sort(
      (a, b) => MEALTIME_ORDER.indexOf(a.mealtime) - MEALTIME_ORDER.indexOf(b.mealtime),
    )
    return {
      iso,
      date: day.date,
      meals: computeTotalMeals(campuses),
      schoolCount: campuses.length,
      blocks,
      milk: mergeCampusesMilk(campuses),
      forecastItems: mergeForecastMenuItems(forecastItems.filter((item) => item.date === iso)),
    }
  })
}

export interface ForecastMenuItem {
  key: string
  name: string
  schoolNames: string[]
  estimatedServings: number | null
  missingHistory: boolean
}

/** Keep estimates independent of packing quantities, and retain unknown demand. */
export function mergeForecastMenuItems(items: ReadonlyArray<ShoppingListForecastRecipe>): ForecastMenuItem[] {
  const groups = new Map<string, ForecastMenuItem>()
  for (const item of items) {
    const key = JSON.stringify([item.componentId, item.componentVariantId ?? null])
    const group = groups.get(key) ?? {
      key,
      name: item.componentName,
      schoolNames: [],
      estimatedServings: null,
      missingHistory: false,
    }
    if (!group.schoolNames.includes(item.schoolName)) {
      group.schoolNames.push(item.schoolName)
    }
    if (item.estimatedServings === null) {
      group.missingHistory = true
    } else {
      group.estimatedServings = (group.estimatedServings ?? 0) + item.estimatedServings
    }
    groups.set(key, group)
  }
  return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name))
}
